import React, { useState, useEffect } from "react";
import {Row, Button, Modal, Form, notification} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import axios from "axios";

import { URLS } from "../../constants";
import { useModal } from "../../hooks";
import { TagTypeForm } from "../../components/Forms";
import { ContentInner, StyledTable } from "../../pages/styled";

export default function TagTypes({ token, environment }) {
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [newTagType, setNewTagType] = useState({});
  const [currentTagType, setCurrentTagType] = useState({});
  const [itemToDelete, setItemToDelete] = useState(null);

  const initialValues = { name: "", description: "" };
  const [isModalShown, setModalShown, onModalOkAction, onModalCancelAction] =
    useModal();
  const [
    isEditModalShown,
    setEditModalShown,
    onEditModalOkAction,
    onEditModalCancelAction,
  ] = useModal();
  const [
    isDeleteModalShown,
    setDeleteModalShown,
    onDeleteModalOkAction,
    onDeleteModalCancelAction,
  ] = useModal();

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 250,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      align: "center",
      with: 250,
    },
    {
      title: "",
      dataIndex: "edit",
      key: "edit",
      width: 72,
      render: (_, row) => {
        return (
          <Button
            type="link"
            size="small"
            onClick={() => {
              onEditTagType(row);
            }}
          >
            Edit
          </Button>
        );
      },
    },
    {
      title: "",
      dataIndex: "delete",
      key: "delete",
      width: 88,
      render: (_, row) => {
        return (
          <Button
            danger
            type="link"
            size="small"
            onClick={() => {
              setItemToDelete(row);
              setDeleteModalShown()();
            }}
          >
            Delete
          </Button>
        );
      },
    },
  ];

  const getTagTypes = async () => {
    axios
      .get(URLS.FETCH_TAG_TYPES, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          env: environment,
        },
      })
      .then((res) => {
        setData(res.data);
      });
  };

  const deleteTagTypeById = async () => {
    return axios
      .delete(`${URLS.FETCH_TAG_TYPES}/${itemToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          env: environment,
        },
      })
      .then((res) => res.data);
  };

  const onDelete = async () => {
    try {
      const response = await deleteTagTypeById();

      if (response) {
        const newData = data.filter((d) => {
          return d.id !== itemToDelete.id;
        });

        setData(newData);
      }
    } catch (e) {
      const message = e?.response?.data?.message || e?.message
      notification.error({
        message: 'Request error',
        description: message
      });
    }
  };

  const handleOk = () => {
    onDelete(itemToDelete);
    onDeleteModalOkAction()();
  };

  const onEditTagType = (data) => {
    setCurrentTagType({ ...data });
    setEditModalShown()();
  };

  const updateTagType = (name, value, isNew) => {
    if (isNew) {
      setNewTagType({ ...newTagType, [name]: value });
    } else {
      setCurrentTagType({ ...currentTagType, [name]: value });
    }
  };

  const onInputChange = (isNew) => (e) => {
    const { name, value } = e.target;
    updateTagType(name, value, isNew);
  };

  const resetCurrentTagType = () => {
    setCurrentTagType(initialValues);
  };

  const showCreateModal = () => {
    resetCurrentTagType();
    setModalShown()();
  };

  const createTagType = async () => (
    axios
    .post(URLS.FETCH_TAG_TYPES, newTagType, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        env: environment,
      },
    })
    .then((res) => res.data)
  );

  const editTagType = async () => (
    axios
    .put(`${URLS.FETCH_TAG_TYPES}/${currentTagType.id}`, currentTagType, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        env: environment,
      },
    })
    .then((res) => res.data)
  );

  const onFinishAdd = async () => {
    try {
      onModalOkAction()();
      const response = await createTagType();

      if (response) {
        setNewTagType({});
        setData([response, ...data])
      }
    } catch (e) {
      const message = e?.response?.data?.message || e?.message
      notification.error({
        message: 'Request error',
        description: message
      });
    }
  };

  const onFinishEdit = async () => {
    try {
      onEditModalOkAction()();
      const response = await editTagType();

      if (response) {
        const newData = data.map((item) => {
          if (response.id === item.id) {
            return {...response};
          }
          return item;
        });
        setData(newData);
      }
    } catch (e) {
      onModalOkAction()();
      const response = await createTagType();

      if (response) {
        setNewTagType({});
        setData([response, ...data])
      }
    }
  };

  useEffect(() => {
    if (!data.length) {
      getTagTypes();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length]);

  return (
    <>
      <Modal
        title="Delete tag type"
        visible={isDeleteModalShown}
        onOk={handleOk}
        onCancel={onDeleteModalCancelAction()}
      >
        Are you sure you want to delete tag type?
      </Modal>
      <Modal
        title="Create new tag type"
        okText="Create tag type"
        visible={isModalShown}
        onOk={() => {}}
        onCancel={() => {
          onModalCancelAction()();
        }}
        okButtonProps={{
          form: "tag-type-create-form",
          key: "submit",
          htmlType: "submit",
        }}
      >
        <TagTypeForm
          form={form}
          onFinish={onFinishAdd}
          onInputChange={onInputChange(true)}
        />
      </Modal>

      <Modal
        title="Edit tag type"
        okText="Save changes"
        visible={isEditModalShown}
        onOk={() => {}}
        onCancel={() => {
          onEditModalCancelAction()();
          setCurrentTagType({});
        }}
        okButtonProps={{
          form: "tag-type-edit-form",
          key: "submit",
          htmlType: "submit",
        }}
      >
        <TagTypeForm
          tagModel={currentTagType}
          isEdit={true}
          form={form}
          onFinish={onFinishEdit}
          onInputChange={onInputChange()}
        />
      </Modal>
      <ContentInner>
        <Button
          type="default"
          icon={<PlusOutlined />}
          onClick={() => {
            showCreateModal();
          }}
          style={{ marginBottom: "25px" }}
        >
          Add new tag type
        </Button>
        <Row justify="center" width="100%">
          <StyledTable
            style={{ width: "100%" }}
            columns={columns}
            rowKey="id"
            dataSource={data}
            pagination={false}
            tableLayout="fixed"
          />
        </Row>
      </ContentInner>
    </>
  );
}
